/**
 * FieldMappingPanel
 * -----------------
 * Renders the source-column → Panorix canonical-field mapping UI used by
 * the schedule Import / Export dialogs.
 *
 * Props
 *   columns:        string[]                 — source column names (from preview)
 *   fields:         { key, label, required, category, type }[]  — canonical schema
 *   value:          { [sourceCol]: canonicalKey }
 *   onChange:       (newValue) => void
 *   unmappedRequired?: string[]              — canonical keys still missing
 *   savedTemplates?: SavedMapping[]          — for "Load template" dropdown
 *   onLoadTemplate?: (tpl)  => void
 *   onSaveTemplate?: (name, setDefault) => Promise
 *   onDeleteTemplate?: (id) => Promise
 */
import React, { useMemo, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Save, AlertTriangle, Trash2, Wand2, FileDown } from 'lucide-react';

const FieldMappingPanel = ({
  columns = [],
  fields = [],
  value = {},
  onChange,
  unmappedRequired = [],
  savedTemplates = [],
  onLoadTemplate,
  onSaveTemplate,
  onDeleteTemplate,
  onAutoDetect,
  onDownloadSavedTemplate,
  onDownloadDraftTemplate,
}) => {
  const [saveName, setSaveName] = useState('');
  const [setDefault, setSetDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  const categorised = useMemo(() => {
    const groups = {};
    for (const f of fields) {
      const cat = f.category || 'Other';
      (groups[cat] = groups[cat] || []).push(f);
    }
    return groups;
  }, [fields]);

  const setMapping = (src, target) => {
    const next = { ...value };
    if (!target || target === '__none__') {
      delete next[src];
    } else {
      next[src] = target;
    }
    onChange?.(next);
  };

  const mappedCount = Object.values(value).filter(Boolean).length;
  const sourceCount = columns.length;

  return (
    <div className="space-y-3" data-testid="field-mapping-panel">
      {/* Header strip */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-900/70 rounded border border-slate-700">
        <div className="flex items-center gap-3 text-xs">
          <span className="text-slate-300 font-medium">Field Mapping</span>
          <Badge variant="outline" className="border-emerald-500 text-emerald-400">
            {mappedCount}/{sourceCount} mapped
          </Badge>
          {unmappedRequired.length > 0 && (
            <Badge variant="outline" className="border-amber-500 text-amber-400" data-testid="missing-required-badge">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {unmappedRequired.length} required missing
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onDownloadDraftTemplate && (
            <Button
              size="sm"
              variant="outline"
              onClick={onDownloadDraftTemplate}
              disabled={Object.keys(value).length === 0}
              className="border-emerald-600 text-emerald-400 h-7 text-xs hover:bg-emerald-600/10"
              data-testid="download-draft-template-btn"
              title="Download a pre-filled .xlsx based on this draft mapping"
            >
              <FileDown className="w-3 h-3 mr-1" />
              Download Template
            </Button>
          )}
          {onAutoDetect && (
            <Button
              size="sm"
              variant="outline"
              onClick={onAutoDetect}
              className="border-slate-600 h-7 text-xs"
              data-testid="auto-detect-btn"
            >
              <Wand2 className="w-3 h-3 mr-1" />
              Auto-detect
            </Button>
          )}
        </div>
      </div>

      {/* Load saved template */}
      {savedTemplates.length > 0 && (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs text-slate-400 mb-1 block">Load saved mapping</Label>
            <Select
              onValueChange={(id) => {
                const tpl = savedTemplates.find((t) => t.id === id);
                if (tpl) onLoadTemplate?.(tpl);
              }}
            >
              <SelectTrigger className="bg-slate-900 border-slate-600 h-8 text-xs" data-testid="load-template-select">
                <SelectValue placeholder="Choose a saved mapping…" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {savedTemplates.map((tpl) => (
                  <SelectItem key={tpl.id} value={tpl.id} className="text-slate-200 text-xs">
                    <div className="flex items-center justify-between w-full gap-3">
                      <span>{tpl.name}</span>
                      {tpl.is_default && (
                        <span className="text-emerald-400 text-[10px]">default</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Mapping grid */}
      <ScrollArea className="h-64 border border-slate-700 rounded">
        <table className="w-full text-xs" data-testid="mapping-table">
          <thead className="sticky top-0 bg-slate-900/95 backdrop-blur z-10">
            <tr className="text-slate-400">
              <th className="text-left p-2 font-medium">Source Column</th>
              <th className="text-left p-2 font-medium">→</th>
              <th className="text-left p-2 font-medium">Panorix Field</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((src) => {
              const tgt = value[src] || '';
              return (
                <tr key={src} className="border-t border-slate-800" data-testid={`mapping-row-${src}`}>
                  <td className="p-2 text-slate-200 font-mono">{src}</td>
                  <td className="p-2 text-slate-500">→</td>
                  <td className="p-2">
                    <Select
                      value={tgt || '__none__'}
                      onValueChange={(v) => setMapping(src, v)}
                    >
                      <SelectTrigger
                        className="bg-slate-900 border-slate-600 h-7 text-xs"
                        data-testid={`mapping-select-${src}`}
                      >
                        <SelectValue placeholder="— skip —" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 max-h-72">
                        <SelectItem value="__none__" className="text-slate-400 text-xs italic">
                          — skip this column —
                        </SelectItem>
                        {Object.entries(categorised).map(([cat, list]) => (
                          <React.Fragment key={cat}>
                            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500">
                              {cat}
                            </div>
                            {list.map((f) => (
                              <SelectItem
                                key={f.key}
                                value={f.key}
                                className="text-slate-200 text-xs"
                              >
                                {f.label}
                                {f.required && <span className="text-red-400 ml-1">*</span>}
                              </SelectItem>
                            ))}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollArea>

      {/* Missing required hint */}
      {unmappedRequired.length > 0 && (
        <div className="p-2 bg-amber-900/20 rounded border border-amber-500/50 text-xs text-amber-300">
          Missing required canonical fields: {unmappedRequired.join(', ')}
        </div>
      )}

      {/* Save as template */}
      {onSaveTemplate && (
        <div className="flex items-end gap-2 pt-2 border-t border-slate-800">
          <div className="flex-1">
            <Label className="text-xs text-slate-400 mb-1 block">Save mapping as template</Label>
            <Input
              placeholder="e.g. P6 Standard Export"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className="bg-slate-900 border-slate-600 h-8 text-xs"
              data-testid="save-mapping-name-input"
            />
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Checkbox
              id="map-default"
              checked={setDefault}
              onCheckedChange={setSetDefault}
              data-testid="set-default-checkbox"
            />
            <Label htmlFor="map-default" className="text-xs text-slate-300 cursor-pointer">
              Default
            </Label>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!saveName.trim() || saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onSaveTemplate(saveName.trim(), setDefault);
                setSaveName('');
                setSetDefault(false);
              } finally {
                setSaving(false);
              }
            }}
            className="border-emerald-600 text-emerald-400 h-8 text-xs"
            data-testid="save-mapping-btn"
          >
            <Save className="w-3 h-3 mr-1" />
            Save
          </Button>
        </div>
      )}

      {/* Saved template list with delete */}
      {savedTemplates.length > 0 && onDeleteTemplate && (
        <details className="text-xs text-slate-400">
          <summary className="cursor-pointer hover:text-slate-200">
            Manage saved mappings ({savedTemplates.length})
          </summary>
          <div className="mt-2 space-y-1 pl-2 border-l border-slate-700">
            {savedTemplates.map((tpl) => (
              <div
                key={tpl.id}
                className="flex items-center justify-between gap-2"
                data-testid={`saved-tpl-${tpl.id}`}
              >
                <span className="text-slate-300">
                  {tpl.name}
                  {tpl.is_default && (
                    <span className="ml-2 text-[10px] text-emerald-400">[default]</span>
                  )}
                  <span className="ml-2 text-[10px] text-slate-500">
                    {tpl.format} / {tpl.direction}
                  </span>
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-emerald-400 hover:text-emerald-300"
                  onClick={() => onDownloadSavedTemplate?.(tpl.id)}
                  disabled={!onDownloadSavedTemplate}
                  data-testid={`download-tpl-${tpl.id}`}
                  title="Download a pre-filled .xlsx using this saved mapping"
                >
                  <FileDown className="w-3 h-3" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                  onClick={() => onDeleteTemplate(tpl.id)}
                  data-testid={`delete-tpl-${tpl.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default FieldMappingPanel;
